import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CareTreatment, type CareFocus, type Tab } from "@/features/patient/care-treatment/components/CareTreatment";
import { useCareTreatmentData } from "@/features/patient/care-treatment/utils/useCareTreatmentData";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { toastError } from "@/shared/lib/toast";

const TABS: Tab[] = ["visits", "other", "plans", "team", "review"];
/** The link params a header search result uses (careTreatmentLink in usePatientSearch.ts). */
const FOCUS_PARAMS = ["note", "q", "date", "visit", "person", "tab"];

/** ?note=<id>&q=<words>&date=<day> | ?visit=<id> | ?person=<key> | ?tab=<tab> → what to open. */
function focusFrom(params: URLSearchParams): (CareFocus & { date?: string }) | null {
  const note = params.get("note");
  if (note) return { kind: "note", id: note, q: params.get("q") ?? undefined, date: params.get("date") ?? undefined };
  const visit = params.get("visit");
  if (visit) return { kind: "visit", id: visit };
  const person = params.get("person");
  if (person) return { kind: "person", key: person };
  const tab = params.get("tab") as Tab | null;
  return tab && TABS.includes(tab) ? { kind: "tab", tab } : null;
}

// Visits, notes, the care team and care plans, all from the care timeline.
export function CareTreatmentPage() {
  const timeline = useCareTreatmentData();
  const { notes, loadNoteAt } = timeline;

  // A header search result: read its link once, then drop it from the URL so Back and refresh don't repeat it.
  const [searchParams, setSearchParams] = useSearchParams();
  const [focus, setFocus] = useState<{ focus: CareFocus; key: number; date?: string } | undefined>(undefined);
  const linkKey = FOCUS_PARAMS.map((p) => searchParams.get(p) ?? "").join("|");
  useEffect(() => {
    const target = focusFrom(searchParams);
    if (!target) return;
    const { date, ...rest } = target as CareFocus & { date?: string };
    setFocus((current) => ({ focus: rest as CareFocus, key: (current?.key ?? 0) + 1, date }));
    setSearchParams((params) => {
      FOCUS_PARAMS.forEach((p) => params.delete(p));
      return params;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkKey]);

  // An older note than the pages loaded: fetch the page that starts on its day. If it isn't
  // there either (or there's no day to look on), say so instead of waiting forever.
  const wantedNote = focus?.focus.kind === "note" ? focus.focus.id : null;
  const missing = wantedNote !== null && !timeline.isLoading && !timeline.isError && !notes.some((n) => n.id === wantedNote);
  useEffect(() => {
    if (!missing || !focus) return;
    const notFound = () => {
      toastError("We couldn't find that note. It may have been removed.");
      setFocus(undefined);
    };
    if (!focus.date) {
      notFound();
      return;
    }
    loadNoteAt(focus.date).then((page) => {
      if (page === null) setFocus(undefined); // the load failed; its own toast said so
      else if (!page.some((n) => n.id === wantedNote)) notFound();
    });
    // Once per search result; loadNoteAt changes identity as pages arrive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missing, focus?.key]);

  // One screen per profile: switching profile starts clean (no other person's filter, open visit or note).
  const { activeDependent } = useFamily();

  return (
    <CareTreatment
      key={activeDependent?.id ?? "self"}
      encounters={timeline.encounters}
      notes={notes}
      documents={timeline.documents}
      stillProcessing={timeline.stillProcessing}
      counts={timeline.counts}
      identityPending={timeline.identityPending}
      careTeam={timeline.careTeam}
      carePlans={timeline.carePlans}
      hasOlder={timeline.hasOlder}
      loadingOlder={timeline.loadingOlder}
      onLoadOlder={timeline.loadOlder}
      onNeedNotesFor={(day) => void loadNoteAt(day)}
      isLoading={timeline.isLoading}
      isError={timeline.isError}
      onRetry={timeline.retry}
      focus={focus}
    />
  );
}
